import dtUtils, { DataTablesSettings } from '../common/datatables-utils';
import handlebarsUtils from '../common/handlebars-utils';
import dateUtils from '../common/date-utils';
import icons from '../common/site-icons';

export enum SearchMode {
	SubmittedForApproval = 'SubmittedForApproval',
	Approved = 'Approved',
	InProgress = 'InProgress',
	Expired = 'Expired',
}

export interface RequestAdminSearchOptions {
	dataTableSelector: string;
	actionColData: string;
	additionalColumns?: DataTables.ColumnSettings[];
	getRowDisplayTitle: (rowData: any) => string;
	getBusinessDaysSinceApprovalForItem?: (rowData: any) => number;
	isItemPublished?: (rowData: any) => boolean;
}

export class RequestAdminSearch {
	private readonly actionButtonTemplate: Handlebars.TemplateDelegate;
	private currentSearchMode = SearchMode.InProgress;
	private searchOptions: RequestAdminSearchOptions = {
		actionColData: 'id',
		dataTableSelector: '.data-tables',
		getRowDisplayTitle: () => ''
	};

	constructor () {
		this.actionButtonTemplate = handlebarsUtils.compileTemplateFrom('ActionButtonTemplate');
	}

	get searchMode () {
		return this.currentSearchMode;
	}

	init (options: RequestAdminSearchOptions) {
		this.searchOptions = options;
		const $dt = $(this.searchOptions.dataTableSelector);
		this.currentSearchMode = $dt.data('search-mode');

		this.initDataTables($dt);
	}

	buildTitleColumn (dataPropName: string): DataTables.ColumnSettings {
		return {
			name: 'Title',
			data: dataPropName,
			title: 'Title',
			createdCell: (td: Node, cellData: any, rowData: any) => {
				if (this.searchMode === SearchMode.Approved && !this.isItemPublished(rowData)) {
					$(td).append($('<span class="ml-1">').addClass('badge badge-secondary').text('hidden'));
				}
			}
		};
	}

	private initDataTables ($dt: JQuery<HTMLElement>) {
		icons.registerSiteIcons();
		const searchUrl = $dt.data('searchUrl');
		const displayInstitution = $dt.data('display-institution').toLowerCase() === 'true';
		const searchMode = this.currentSearchMode;

		const settings: DataTablesSettings = {
			autoWidth: true,
			serverSide: true,
			processing: true,
			order: [[1, 'asc']],
			ajax: dtUtils.makeAjaxPostConfig(searchUrl, { searchMode }),
			renderer: 'bootstrap',
			columns: this.getColumns(searchMode, displayInstitution),
			debounce: 800,
			language: {
				searchPlaceholder: $('#search-placeholder').text()
			},
			initComplete: () => $('#search-tips').removeClass('d-none')
		};
		dtUtils.persistStateToSessionStorage(settings, () => this.initDataTables($dt));

		dtUtils.createDataTable($dt, settings);
	}

	private buildSelectorParams (api: DataTables.Api, cols: DataTables.ColumnSettings[]): string {
		const search = api.search();
		const order = api.order()[0];
		const columnIndex: number = (order[0] as unknown) as number;
		const sortColumn = cols[columnIndex].name;
		const sortDirection = order[1] as string;
		const pageInfo = api.page.info();

		const selectorParams = btoa(`${search}|${sortColumn}|${sortDirection}|${pageInfo.start}|${pageInfo.length}`);
		return selectorParams;
	}

	private getRowDisplayTitle (rowData: any): string {
		const getRowDisplayTitle = this.searchOptions.getRowDisplayTitle;
		return getRowDisplayTitle ? getRowDisplayTitle(rowData) : '';
	}

	private getBusinessDaysSinceApprovalForItem (rowData: any): number {
		const getDays = this.searchOptions.getBusinessDaysSinceApprovalForItem;
		return getDays ? getDays(rowData) : 0;
	}

	private isItemPublished (rowData: any): boolean {
		const isItemPublished = this.searchOptions.isItemPublished;
		return isItemPublished ? isItemPublished(rowData) : false;
	}

	private getColumns (searchMode: SearchMode, displayInstitution: boolean): DataTables.ColumnSettings[] {
		const self = this;
		let cols: DataTables.ColumnSettings[] = [];
		const options = this.searchOptions;

		const actionCol = {
			name: 'Id',
			data: options.actionColData,
			title: '',
			createdCell (td: Node, cellData: any, rowData: any) {
				const api: DataTables.Api = $(this).DataTable();

				const templateData = Object.assign({
					s: self.buildSelectorParams(api, cols)
				}, rowData);

				dtUtils.createActionButtonTemplate(self.actionButtonTemplate, td, templateData, 'confirmMessageModal');
			},
			orderable: false
		};

		const institutionNameCol: DataTables.ColumnSettings = {
			name: 'InstitutionName',
			data: 'institutionName',
			title: 'Institution Name'
		};

		const currentStateDateCol: DataTables.ColumnSettings = {
			name: 'CurrentRevisionStateDate',
			data: 'currentRevisionStateDate',
			title: (searchMode === SearchMode.SubmittedForApproval) ? 'Submitted Date' : 'Status Date',
			createdCell: (node: Node, cellData: any, rowData: any) => {
				const td = node as HTMLTableCellElement;
				td.childNodes.forEach((n) => td.removeChild(n));

				const stateDate = dateUtils.displayDateTime(cellData);
				const stateDateDisplay = document.createElement('span');
				stateDateDisplay.innerText = stateDate;
				td.appendChild(stateDateDisplay);

				if (searchMode === SearchMode.SubmittedForApproval) {
					const businessDaysSinceApproval = this.getBusinessDaysSinceApprovalForItem(rowData);
					const minWarningThreshold = 3; // business days
					const minDangerThreshold = 7;

					if (businessDaysSinceApproval >= minWarningThreshold) {
						const iconContainer = document.createElement('abbr');
						const icon = document.createElement('i');
						const badgeClass = businessDaysSinceApproval > minDangerThreshold ? 'badge-danger' : 'badge-warning';
						const exceeds = businessDaysSinceApproval > minDangerThreshold ? 'has exceeded' : 'is approaching';

						icon.classList.add('fas', 'fa-exclamation-triangle');
						iconContainer.classList.add(badgeClass, 'badge', 'p-1', 'd-inline-block', 'mr-1', 'small');
						iconContainer.append(icon);

						const rowDisplayTitle = this.getRowDisplayTitle(rowData);
						iconContainer.title =
							`'${rowDisplayTitle}' ${exceeds} the desired max response time of ${minDangerThreshold} business days.`;

						td.insertBefore(iconContainer, stateDateDisplay);
						td.classList.add('text-nowrap');
					}
				}
			}
		};

		const currentStateCol: DataTables.ColumnSettings = {
			name: 'CurrentRevisionStateDisplay',
			data: 'currentRevisionStateDisplay',
			title: 'Status'
		};

		const approvedStateCol: DataTables.ColumnSettings = {
			name: 'ApprovedRevisionStateDisplay',
			data: 'approvedRevisionStateDisplay',
			title: 'Status'
		};

		const approvedStateDateCol: DataTables.ColumnSettings = {
			name: 'ApprovedRevisionStateDate',
			data: 'approvedRevisionStateDate',
			title: (searchMode === SearchMode.Expired) ? 'Expiry Date' : 'Approved Date',
			createdCell: (td: Node, cellData: any) => {
				const stateDate = dateUtils.displayDateTime(cellData);
				$(td).text(stateDate);
			}
		};

		const expirationDateCol: DataTables.ColumnSettings = {
			name: 'ApprovedRevisionExpirationDate',
			data: 'approvedRevisionExpirationDateDisplay',
			title: 'Expiry Date'
		};

		const additionalCols = options.additionalColumns || [];

		switch (searchMode) {
			case SearchMode.Approved:
				cols = [
					actionCol
				];
				if (displayInstitution) {
					cols.push(institutionNameCol);
				}
				cols.push(...additionalCols,
					{
						name: 'ApprovalDate',
						data: 'approvalDate',
						title: 'Approval Date',
						createdCell: (td: Node, cellData: any) => {
							const approvalDate = dateUtils.displayDateTime(cellData);
							$(td).text(approvalDate);
							(td as HTMLTableCellElement).classList.add('text-nowrap');
						}
					},
					expirationDateCol
				);
				break;

			case SearchMode.SubmittedForApproval:
				cols = [
					actionCol,
					currentStateDateCol
				];

				if (displayInstitution) {
					cols.push(institutionNameCol);
				}
				cols.push(...additionalCols);
				break;

			case SearchMode.InProgress:
				cols = [
					actionCol,
					currentStateDateCol,
					currentStateCol];

				if (displayInstitution) {
					cols.push(institutionNameCol);
				}
				cols.push(...additionalCols);
				break;

			case SearchMode.Expired:
				cols = [
					actionCol,
					approvedStateCol,
					approvedStateDateCol
				];

				if (displayInstitution) {
					cols.push(institutionNameCol);
				}
				cols.push(...additionalCols);
				break;

			default:
				// eslint:disable-next-line:no-console
				console.error(`Unrecognized search mode ${searchMode}`);
		}

		return cols;
	}
}
